<template>
  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" class="relative z-[140]" @close="closeDialog">
      <TransitionChild
        as="template"
        enter="duration-200 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-150 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-slate-950/45" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4">
          <TransitionChild
            as="template"
            enter="duration-200 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-150 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
              <DialogTitle as="h2" class="text-xl font-bold text-slate-900">
                Nasıl görüşmek istersiniz?
              </DialogTitle>
              <p class="mt-1 text-sm leading-5 text-slate-600">
                Hemen arayabilir veya numaranızı bırakarak sizi aramamızı isteyebilirsiniz.
              </p>

              <button
                type="button"
                class="mt-5 flex w-full items-center justify-center gap-3 rounded-2xl bg-emerald-600 px-5 py-4 text-left text-white shadow-lg shadow-emerald-200 transition hover:-translate-y-0.5 hover:bg-emerald-700 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-emerald-200"
                @click="callDirectly"
              >
                <span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/20">
                  <font-awesome-icon icon="fa-solid fa-phone" class="h-5 w-5" />
                </span>
                <span>
                  <span class="block text-lg font-extrabold leading-5">Hemen Ara</span>
                  <span class="mt-1 block text-xs font-medium text-emerald-50">Numara girmeden telefonunuzun arama ekranını açar</span>
                </span>
              </button>

              <div v-if="status !== 'success'" class="my-5 flex items-center gap-3" aria-hidden="true">
                <span class="h-px flex-1 bg-slate-200"></span>
                <span class="text-xs font-semibold uppercase tracking-wide text-slate-400">veya sizi biz arayalım</span>
                <span class="h-px flex-1 bg-slate-200"></span>
              </div>

              <form v-if="status !== 'success'" class="space-y-4" @submit.prevent="requestCallback">
                <label class="block">
                  <span class="mb-1 block text-sm font-semibold text-slate-700">Telefon numaranız</span>
                  <input
                    v-model="phone"
                    type="tel"
                    inputmode="tel"
                    autocomplete="tel"
                    placeholder="05xx xxx xx xx"
                    class="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-base outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                  />
                </label>

                <label class="flex items-start gap-2 text-xs leading-5 text-slate-600">
                  <input v-model="contactConsent" type="checkbox" class="mt-1 rounded border-slate-300 text-sky-600" />
                  <span>Bu numaradan aranmayı ve talebimin yanıtlanması amacıyla iletişim bilgilerimin işlenmesini kabul ediyorum.</span>
                </label>

                <p v-if="errorMessage" class="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {{ errorMessage }}
                </p>

                <button
                  type="submit"
                  :disabled="loading"
                  class="w-full rounded-xl bg-sky-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-700 disabled:cursor-wait disabled:opacity-60"
                >
                  {{ loading ? 'Arama hazırlanıyor...' : 'Beni ara' }}
                </button>
              </form>

              <div v-else class="mt-5 rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800">
                <p class="font-bold">Arama başlatıldı.</p>
                <p class="mt-1">Telefonunuz kısa süre içinde çalacak. Lütfen aramayı yanıtlayın.</p>
              </div>

              <div class="mt-4 flex justify-end">
                <button type="button" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50" @click="closeDialog">
                  Kapat
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import axios from 'axios'
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

const config = useRuntimeConfig()
const route = useRoute()
const { createRecaptcha } = useCommon()
const { getAttributionPayload, trackEvent } = useTracking()
const { open, destination, closeDialog: closePublicCallDialog } = usePublicCallActions()

const phone = ref('')
const contactConsent = ref(false)
const loading = ref(false)
const status = ref<'idle' | 'success'>('idle')
const errorMessage = ref('')

const closeDialog = () => {
  closePublicCallDialog()
  setTimeout(() => {
    status.value = 'idle'
    errorMessage.value = ''
  }, 200)
}

const callDirectly = () => {
  if (!destination.value) return
  trackEvent('direct_phone_call', {
    page_path: window.location.pathname,
    tour_id: String(route.params.id || route.params.link || ''),
  })
  window.location.href = `tel:${destination.value}`
  closeDialog()
}

const requestCallback = async () => {
  errorMessage.value = ''
  if (!phone.value.trim()) {
    errorMessage.value = 'Telefon numaranızı girin.'
    return
  }
  if (!contactConsent.value) {
    errorMessage.value = 'Aranma onayı vermelisiniz.'
    return
  }

  loading.value = true
  try {
    let token = await createRecaptcha()
    if (!token) {
      await new Promise((resolve) => setTimeout(resolve, 1500))
      token = await createRecaptcha()
    }
    const tourId = String(route.params.id || route.params.link || '')
    const response = await axios.post(`${config.public.apiBase}/verimor/callback`, {
      phone: phone.value,
      contactConsent: true,
      token,
      attribution: getAttributionPayload(),
      tourId,
      page: window.location.pathname,
    })
    if (response.data?.success !== true) throw new Error(response.data?.message || 'Arama başlatılamadı.')

    status.value = 'success'
    trackEvent('callback_requested', {
      call_id: String(response.data.callUuid || ''),
      page_path: window.location.pathname,
      tour_id: tourId,
    })
  } catch (error: any) {
    errorMessage.value = error?.response?.data?.message || error?.message || 'Arama başlatılamadı.'
  } finally {
    loading.value = false
  }
}
</script>
